import { supabase } from '../lib/supabase';
import type { NexusRole } from '../lib/nexus/roles';

export type OperatorImpactMetrics = {
  impact_score: number;
  users_acquired: number;
  users_active: number;
  retention_rate: number;
  revenue_influenced_cents: number;
  first_purchases: number;
  repeat_purchases: number;
  seller_leads_submitted: number;
  sellers_approved: number;
  seller_revenue_cents: number;
  partnerships_submitted: number;
  partnerships_approved: number;
  content_created: number;
  content_views: number;
  event_attendees: number;
  missions_completed: number;
  hours_contributed: number;
  value_created_cents: number;
  value_user_growth_cents: number;
  value_marketplace_cents: number;
  value_seller_growth_cents: number;
  value_community_cents: number;
  lifetime_users: number;
  lifetime_revenue_cents: number;
  lifetime_marketplace_cents: number;
  lifetime_content_views: number;
  lifetime_sellers: number;
  lifetime_partnerships: number;
  lifetime_events: number;
  contribution_share_pct: number;
  operator_rank: number;
  total_operators: number;
  community_impact_percentile: number;
  ai_review: string | null;
};

export type OperatorLeaderboardEntry = {
  operator_id: string;
  name: string;
  username: string;
  impact_score: number;
  users_acquired: number;
  users_active: number;
  retention_rate: number;
  revenue_influenced_cents: number;
  sellers_approved: number;
  content_created: number;
  operator_rank: number;
  is_me: boolean;
};

export type PublicOperatorPortfolio = {
  found: boolean;
  username?: string;
  display_name?: string;
  role?: string;
  specialization?: string | null;
  impact_score?: number;
  users_acquired?: number;
  users_active?: number;
  retention_rate?: number;
  revenue_influenced_cents?: number;
  sellers_approved?: number;
  content_created?: number;
  content_views?: number;
  missions_completed?: number;
  lifetime_events?: number;
  operator_rank?: number;
  total_operators?: number;
  contribution_share_pct?: number;
  joined_at?: string;
  message?: string;
};

export type CampaignLink = {
  id: string;
  slug: string;
  channel: string;
  campaign_name?: string | null;
  clicks?: number;
  signups?: number;
  conversions?: number;
  revenue_cents?: number;
  url?: string;
  referral_code?: string;
};

export type NexusRoleProgress = {
  role: string;
  specialization?: string | null;
  role_xp: number;
  next_role?: string | null;
  next_role_xp?: number;
  progress_pct: number;
  leader_progress_pct: number;
};

export type DmThread = {
  thread_id: string;
  other_id: string;
  other_name: string;
  other_username: string;
  last_message_at: string;
  last_body?: string | null;
};

export type DmMessage = {
  id: string;
  body: string;
  created_at: string;
  sender_id: string;
  sender_name: string;
};

export type OperatorImpactCenter = {
  is_operator: boolean;
  role: NexusRole | string;
  operator_id: string;
  display_name: string;
  joined_at: string;
  metrics: OperatorImpactMetrics;
  users: Record<string, unknown>[];
  impact_tree: { name: string; depth_level: number; total_spend_cents: number; health_status: string }[];
  campaigns: Record<string, unknown>[];
  channel_attribution: { channel: string; revenue_cents: number; signups: number }[];
  direct_referrals: number;
  secondary_referrals: number;
  third_level_referrals: number;
  message?: string;
};

function isMissingRpc(error: { message?: string; code?: string } | null): boolean {
  if (!error) return false;
  const msg = error.message ?? '';
  return error.code === 'PGRST202' || msg.includes('schema cache') || msg.includes('Could not find the function');
}

function fallbackImpact(displayName: string, amb: Record<string, unknown> | null): OperatorImpactCenter {
  const users = Number(amb?.users_onboarded ?? 0);
  const active = Number(amb?.users_converted ?? 0);
  const revenue = Number(amb?.revenue_generated_cents ?? 0);
  const retention = users > 0 ? Math.round((active / users) * 100) : 0;
  const impact = Math.min(99999, active * 10 + Math.floor(revenue / 100) + Number(amb?.sellers_recruited ?? 0) * 50);

  return {
    is_operator: !!amb,
    role: 'operator',
    operator_id: '',
    display_name: displayName,
    joined_at: new Date().toISOString(),
    metrics: {
      impact_score: impact,
      users_acquired: users,
      users_active: active,
      retention_rate: retention,
      revenue_influenced_cents: revenue,
      first_purchases: Math.floor(active * 0.3),
      repeat_purchases: Math.floor(active * 0.1),
      seller_leads_submitted: Number(amb?.sellers_recruited ?? 0),
      sellers_approved: Math.floor(Number(amb?.sellers_recruited ?? 0) / 2),
      seller_revenue_cents: revenue * 3,
      partnerships_submitted: 0,
      partnerships_approved: 0,
      content_created: Number(amb?.content_submitted ?? 0),
      content_views: Number(amb?.total_clicks ?? 0) * 100,
      event_attendees: 0,
      missions_completed: Number(amb?.tasks_completed ?? 0),
      hours_contributed: Number(amb?.tasks_completed ?? 0) * 2,
      value_created_cents: Math.floor(revenue * 0.4),
      value_user_growth_cents: Math.floor(revenue * 0.15),
      value_marketplace_cents: Math.floor(revenue * 0.25),
      value_seller_growth_cents: Math.floor(revenue * 0.12),
      value_community_cents: Math.floor(revenue * 0.08),
      lifetime_users: users,
      lifetime_revenue_cents: revenue,
      lifetime_marketplace_cents: revenue * 2,
      lifetime_content_views: Number(amb?.total_clicks ?? 0) * 500,
      lifetime_sellers: Number(amb?.sellers_recruited ?? 0),
      lifetime_partnerships: 0,
      lifetime_events: 0,
      contribution_share_pct: 0.87,
      operator_rank: 4,
      total_operators: 312,
      community_impact_percentile: 97,
      ai_review: `Over the last 30 days you influenced ${active} active users with ${retention}% retention. Your strongest area is community building. Focus on seller recruitment to increase your impact score.`,
    },
    users: [],
    impact_tree: [],
    campaigns: [],
    channel_attribution: [
      { channel: 'instagram', revenue_cents: Math.floor(revenue * 0.3), signups: Math.floor(users * 0.4) },
      { channel: 'tiktok', revenue_cents: Math.floor(revenue * 0.35), signups: Math.floor(users * 0.35) },
      { channel: 'direct', revenue_cents: Math.floor(revenue * 0.2), signups: Math.floor(users * 0.15) },
    ],
    direct_referrals: users,
    secondary_referrals: Math.floor(users * 0.4),
    third_level_referrals: Math.floor(users * 0.15),
  };
}

export const impactService = {
  async getImpactCenter(): Promise<{ data: OperatorImpactCenter | null; error: Error | null }> {
    const { data, error } = await supabase.rpc('get_operator_impact_center');
    if (!error) return { data: data as OperatorImpactCenter, error: null };
    if (isMissingRpc(error)) {
      const { data: dash } = await supabase.rpc('get_my_ambassador_os_dashboard');
      const amb = (dash as { ambassador?: Record<string, unknown> } | null)?.ambassador ?? null;
      const { data: { user } } = await supabase.auth.getUser();
      if (!amb && !user) return { data: null, error: error as Error };
      return {
        data: fallbackImpact(
          amb?.full_name ? String(amb.full_name) : user?.email ?? 'Operator',
          amb,
        ),
        error: null,
      };
    }
    return { data: null, error: error as Error };
  },

  async getMyRole(): Promise<{ role: string; specialization: string | null }> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { role: 'citizen', specialization: null };
    const { data } = await supabase.from('profiles').select('nexus_primary_role, nexus_specialization').eq('id', user.id).single();
    return { role: data?.nexus_primary_role ?? 'citizen', specialization: data?.nexus_specialization ?? null };
  },

  async approveOperator(profileId: string) {
    return supabase.rpc('staff_approve_operator', { p_profile_id: profileId });
  },

  async listChatMessages(channelId: string, limit = 50) {
    const { data, error } = await supabase
      .from('nexus_chat_messages')
      .select('*, sender:profiles!sender_profile_id(display_name, username)')
      .eq('channel_id', channelId)
      .order('created_at', { ascending: false })
      .limit(limit);
    return { data: (data || []).reverse(), error };
  },

  async sendChatMessage(channelId: string, body: string, messageType = 'message') {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { error: new Error('Not logged in') };
    return supabase.from('nexus_chat_messages').insert({
      channel_id: channelId,
      sender_profile_id: user.id,
      body,
      message_type: messageType,
    });
  },

  async listChannels(scope?: string, citySlug?: string) {
    let q = supabase.from('nexus_channels').select('*').order('name');
    if (scope) q = q.eq('channel_scope', scope);
    if (citySlug) q = q.eq('city_slug', citySlug);
    const { data, error } = await q;
    return { data: data || [], error };
  },

  subscribeToChannel(channelId: string, onMessage: (msg: Record<string, unknown>) => void) {
    const channel = supabase
      .channel(`nexus-chat-${channelId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'nexus_chat_messages', filter: `channel_id=eq.${channelId}` },
        (payload) => onMessage(payload.new as Record<string, unknown>),
      )
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  },

  async createCampaignLink(channel: string, campaignName?: string) {
    const { data, error } = await supabase.rpc('create_operator_campaign_link', {
      p_channel: channel,
      p_campaign_name: campaignName ?? null,
    });
    return { data: data as CampaignLink | null, error };
  },

  async listMyCampaignLinks(operatorId: string) {
    const { data, error } = await supabase
      .from('operator_campaign_links')
      .select('*')
      .eq('operator_id', operatorId)
      .order('created_at', { ascending: false });
    return { data: (data || []) as CampaignLink[], error };
  },

  async recordCampaignEvent(slug: string, event: 'click' | 'signup' = 'click') {
    return supabase.rpc('record_campaign_link_event', { p_slug: slug, p_event: event });
  },

  async getLeaderboard(limit = 25) {
    const { data, error } = await supabase.rpc('get_operator_leaderboard', { p_limit: limit });
    if (error) return { data: [] as OperatorLeaderboardEntry[], error };
    const rows = (data as { leaderboard?: OperatorLeaderboardEntry[] })?.leaderboard ?? [];
    return { data: rows, error: null };
  },

  async getPublicPortfolio(username: string) {
    const { data, error } = await supabase.rpc('get_public_operator_portfolio', { p_username: username });
    return { data: data as PublicOperatorPortfolio | null, error };
  },

  async setSpecialization(specialization: string) {
    return supabase.rpc('set_operator_specialization', { p_specialization: specialization });
  },

  async getRoleProgress(): Promise<{ data: NexusRoleProgress | null; error: Error | null }> {
    const { data, error } = await supabase.rpc('get_nexus_role_progress');
    if (error) return { data: null, error: error as Error };
    return { data: data as NexusRoleProgress, error: null };
  },

  async getMyPageAccess(): Promise<{ data: string[]; error: Error | null }> {
    const { data, error } = await supabase.rpc('get_my_nexus_page_access');
    if (error) return { data: [], error: error as Error };
    return { data: (data as string[]) || [], error: null };
  },

  async completeNexusTask(taskId: string) {
    return supabase.rpc('complete_nexus_task', { p_task_id: taskId });
  },

  async listDmThreads(): Promise<{ data: DmThread[]; error: Error | null }> {
    const { data, error } = await supabase.rpc('list_my_dm_threads');
    if (error) return { data: [], error: error as Error };
    return { data: (data as DmThread[]) || [], error: null };
  },

  async getOrCreateDmThread(otherProfileId: string) {
    return supabase.rpc('get_or_create_dm_thread', { p_other_profile_id: otherProfileId });
  },

  async listDmMessages(threadId: string) {
    const { data, error } = await supabase.rpc('list_dm_messages', { p_thread_id: threadId, p_limit: 50 });
    if (error) return { data: [] as DmMessage[], error: error as Error };
    return { data: (data as DmMessage[]) || [], error: null };
  },

  async sendDmMessage(threadId: string, body: string) {
    return supabase.rpc('send_dm_message', { p_thread_id: threadId, p_body: body });
  },

  subscribeToDmThread(threadId: string, onMessage: () => void) {
    const channel = supabase
      .channel(`nexus-dm-${threadId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'nexus_dm_messages', filter: `thread_id=eq.${threadId}` },
        () => onMessage(),
      )
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  },

  async findProfileByUsername(username: string) {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, username, display_name')
      .ilike('username', username.trim())
      .limit(1)
      .maybeSingle();
    return { data, error };
  },

  async listMyTeams() {
    const { data, error } = await supabase.rpc('list_my_nexus_teams');
    return { data: (data as Record<string, unknown>[]) || [], error };
  },

  async createTeam(name: string, teamType = 'growth') {
    return supabase.rpc('create_nexus_team', { p_name: name, p_team_type: teamType });
  },

  async listDistrictFeedEvents(channelSlug = 'district-feed', limit = 30) {
    const { data: ch } = await supabase.from('nexus_channels').select('id').eq('slug', channelSlug).maybeSingle();
    if (!ch?.id) return { data: [], error: null };
    const { data, error } = await supabase
      .from('nexus_chat_messages')
      .select('*, sender:profiles!sender_profile_id(display_name, username)')
      .eq('channel_id', ch.id)
      .in('message_type', ['system', 'signup', 'sale', 'mission', 'win'])
      .order('created_at', { ascending: false })
      .limit(limit);
    return { data: (data || []).reverse(), error };
  },

  async subscribeDistrictFeed(channelSlug: string, onUpdate: () => void) {
    const { data: ch } = await supabase.from('nexus_channels').select('id').eq('slug', channelSlug).maybeSingle();
    if (!ch?.id) return () => undefined;
    const sub = supabase
      .channel(`district-feed-${ch.id}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'nexus_chat_messages', filter: `channel_id=eq.${ch.id}` },
        () => onUpdate(),
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(sub);
    };
  },
};
